import React, { useState, useEffect } from 'react';
import { 
  Box, Container, Typography, Card, CardContent, Grid, Button, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Chip, CircularProgress, Alert, Snackbar, IconButton, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress,
  useMediaQuery, useTheme
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SyncIcon from '@mui/icons-material/Sync';
import InfoIcon from '@mui/icons-material/Info';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import apiClient from '../services/api';
import { exportToExcel } from '../utils/excel';

interface SyncLog {
  id: number;
  sync_type: string;
  status: 'pending' | 'running' | 'success' | 'error';
  started_at: string;
  completed_at: string | null;
  records_processed: number;
  records_created: number;
  records_updated: number;
  records_failed: number;
  error_message: string;
  details: any;
  created_at: string;
}

export const SyncManagerPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [connectionStatus, setConnectionStatus] = useState<'unchecked' | 'connected' | 'disconnected'>('unchecked');
  const [testingConnection, setTestingConnection] = useState(false);
  
  const [logs, setLogs] = useState<SyncLog[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [activeLogId, setActiveLogId] = useState<number | null>(null);

  // Error details modal
  const [errorModal, setErrorModal] = useState<{ open: boolean; title: string; content: string }>({
    open: false,
    title: '',
    content: ''
  });

  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const checkConnection = async () => {
    setTestingConnection(true);
    try {
      const response = await apiClient.get('/moodle/courses/test_connection/');
      if (response.data && response.data.connected) {
        setConnectionStatus('connected');
        setToast({ open: true, message: 'Conexión exitosa con el servidor Moodle.', severity: 'success' });
      } else {
        setConnectionStatus('disconnected');
        setToast({ open: true, message: 'Fallo al conectar: ' + (response.data.message || 'Error desconocido'), severity: 'error' });
      }
    } catch (err: any) {
      setConnectionStatus('disconnected');
      setToast({ open: true, message: 'Error de red al conectar con Moodle: ' + (err.message || ''), severity: 'error' });
    } finally {
      setTestingConnection(false);
    }
  };

  const fetchLogs = async (silent = false) => {
    if (!silent) setLoadingLogs(true);
    try {
      const response = await apiClient.get('/moodle/sync-logs/');
      if (response.data && response.data.results) {
        setLogs(response.data.results);
        
        // If there's an active running/pending task in our local list, keep polling
        const runningTask = response.data.results.find((l: SyncLog) => l.status === 'running' || l.status === 'pending');
        if (runningTask) {
          setActiveLogId(runningTask.id);
        } else {
          setActiveLogId(null);
        }
      }
    } catch (err: any) {
      console.error('Failed to load logs:', err);
    } finally {
      if (!silent) setLoadingLogs(false);
    }
  };

  useEffect(() => {
    checkConnection();
    fetchLogs();
  }, []);

  // Poll for status updates if there's a running task
  useEffect(() => {
    let intervalId: any;
    if (activeLogId) {
      intervalId = setInterval(() => {
        fetchLogs(true);
      }, 3000); // Poll every 3 seconds
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [activeLogId]);

  const triggerSync = async (endpoint: string, payload: any = {}) => {
    try {
      setToast({ open: true, message: 'Iniciando tarea de sincronización asíncrona...', severity: 'success' });
      const response = await apiClient.post(`/moodle/${endpoint}/sync/`, payload);
      
      if (response.data && response.data.success) {
        if (response.data.log_id) {
          setActiveLogId(response.data.log_id);
        }
        await fetchLogs(true);
      }
    } catch (err: any) {
      console.error(err);
      const errMsg = err.response?.data?.error || 'Error al disparar la sincronización.';
      setToast({ open: true, message: errMsg, severity: 'error' });
    }
  };

  const triggerSyncAll = async () => {
    try {
      setToast({ open: true, message: 'Iniciando sincronización completa asíncrona...', severity: 'success' });
      const response = await apiClient.post('/moodle/courses/sync_all/', {});
      
      if (response.data && response.data.success) {
        if (response.data.log_id) {
          setActiveLogId(response.data.log_id);
        }
        await fetchLogs(true);
      }
    } catch (err: any) {
      console.error(err);
      const errMsg = err.response?.data?.error || 'Error al disparar la sincronización completa.';
      setToast({ open: true, message: errMsg, severity: 'error' });
    }
  };

  const handleExportExcel = () => {
    const headers = ['Log ID', 'Tipo Sincronización', 'Estado', 'Procesados', 'Creados', 'Actualizados', 'Fallidos', 'Inicio', 'Fin', 'Mensaje de Error'];
    const keys = ['id', 'sync_type', 'status', 'records_processed', 'records_created', 'records_updated', 'records_failed', 'started_at', 'completed_at', 'error_message'];
    exportToExcel(logs, headers, keys, 'Bitacora_Sincronizaciones_Moodle');
  };

  const openErrorModal = (log: SyncLog) => {
    setErrorModal({
      open: true,
      title: `Error en Sincronización de ${getSyncTypeLabel(log.sync_type)}`,
      content: log.error_message || 'No se registraron detalles del error.'
    });
  };

  const getSyncTypeLabel = (type: string) => {
    const mapping: Record<string, string> = {
      'courses': 'Cursos',
      'enrollments': 'Alumnos y Matrícula',
      'grades': 'Calificaciones',
      'complete_sync': 'Sincronización Completa',
    };
    return mapping[type] || type;
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'success':
        return <Chip icon={<CheckCircleIcon />} label="Exitoso" color="success" size="small" variant="outlined" sx={{ fontWeight: 600 }} />;
      case 'error':
        return <Chip icon={<ErrorIcon />} label="Error" color="error" size="small" variant="outlined" sx={{ fontWeight: 600 }} />;
      case 'running':
        return <Chip icon={<CircularProgress size={14} color="inherit" />} label="Sincronizando" color="warning" size="small" sx={{ fontWeight: 700 }} />;
      case 'pending':
      default:
        return <Chip label="Pendiente" color="default" size="small" variant="outlined" sx={{ fontWeight: 600 }} />;
    }
  };

  const formatDuration = (start: string, end: string | null) => {
    if (!end) return '-';
    const sTime = new Date(start).getTime();
    const eTime = new Date(end).getTime();
    const diff = (eTime - sTime) / 1000;
    return diff >= 60 ? `${Math.round(diff / 60)} min` : `${Math.round(diff)} seg`;
  };

  const activeLog = logs.find(l => l.id === activeLogId);

  return (
    <Container maxWidth="lg" className="fade-in-up" sx={{ py: 4 }}>
      
      <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" mb={4} gap={2}>
        <Box display="flex" alignItems="center">
          <IconButton href="/" sx={{ mr: 2, bgcolor: 'background.paper', border: '1px solid #E2E8F0' }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={800} sx={{ color: '#0A2540' }}>
              Integración y Sincronización Moodle
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Controla y monitoriza las tareas de respaldo académico en segundo plano
            </Typography>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap={2} width={{ xs: '100%', sm: 'auto' }} justifyContent={{ xs: 'space-between', sm: 'flex-end' }}>
          {/* Pulsing Live Connection Radar Indicator */}
          <Box display="flex" alignItems="center" sx={{ bgcolor: 'background.paper', p: 1.5, px: 2.5, borderRadius: 30, border: '1px solid #E2E8F0', height: 48 }}>
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1.5 }}>
              {connectionStatus === 'connected' && (
                <>
                  <Box sx={{ position: 'absolute', width: 22, height: 22, borderRadius: '50%', border: '2px solid #00D9A5', animation: 'pulse-radar 1.5s infinite ease-out' }} />
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#00D9A5' }} />
                </>
              )}
              {connectionStatus === 'disconnected' && (
                <>
                  <Box sx={{ position: 'absolute', width: 22, height: 22, borderRadius: '50%', border: '2px solid #FF5252', animation: 'pulse-radar 1.5s infinite ease-out' }} />
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#FF5252' }} />
                </>
              )}
              {connectionStatus === 'unchecked' && (
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#CBD5E1' }} />
              )}
            </Box>
            <Typography variant="body2" fontWeight={700} sx={{ color: '#0A2540', fontSize: '0.85rem' }}>
              {connectionStatus === 'connected' ? 'Servidor Conectado' : connectionStatus === 'disconnected' ? 'Sin Conexión' : 'Sin Verificar'}
            </Typography>
          </Box>
          
          <Button 
            variant="contained" 
            color="secondary" 
            startIcon={testingConnection ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
            onClick={checkConnection}
            disabled={testingConnection}
            sx={{ borderRadius: 30, px: 3, height: 48 }}
          >
            Probar Ping
          </Button>
        </Box>
      </Box>

      {/* Active Sync Progress Panel */}
      {activeLog && (
        <Card 
          variant="outlined" 
          sx={{ 
            mb: 4, 
            borderColor: 'rgba(0, 112, 224, 0.4)', 
            backgroundColor: 'rgba(0, 112, 224, 0.02)',
            boxShadow: '0 8px 30px rgba(0, 112, 224, 0.05)',
            borderRadius: 4
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" mb={2.5} gap={1}>
              <Box display="flex" alignItems="center">
                <CircularProgress size={24} color="secondary" sx={{ mr: 2 }} />
                <Typography variant="h6" fontWeight={800} color="secondary.dark">
                  Sincronización en curso: {getSyncTypeLabel(activeLog.sync_type)}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Iniciado: {new Date(activeLog.started_at).toLocaleTimeString()}
              </Typography>
            </Box>
            
            {/* Sliding glow scanner progress bar */}
            <LinearProgress color="secondary" sx={{ height: 10, borderRadius: 5, mb: 3 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block" fontWeight={500}>Procesados</Typography>
                <Typography variant="h5" fontWeight={850} color="#0A2540">{activeLog.records_processed}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block" fontWeight={500}>Nuevos</Typography>
                <Typography variant="h5" fontWeight={850} color="#00B074">{activeLog.records_created}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block" fontWeight={500}>Actualizados</Typography>
                <Typography variant="h5" fontWeight={850} color="secondary.main">{activeLog.records_updated}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block" fontWeight={500}>Fotos Guardadas</Typography>
                <Typography variant="h5" fontWeight={850} color="#7C4DFF">{activeLog.details?.photos_downloaded || 0}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Sync trigger actions */}
      <Typography variant="h6" fontWeight={800} sx={{ color: '#0A2540', mb: 2.5 }}>
        Acciones Granulares de Sincronización
      </Typography>
      <Grid container spacing={3} mb={5}>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined" sx={{ height: '100%', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)' } }}>
            <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
              <Box mb={3.5}>
                <Typography variant="subtitle1" fontWeight={750} sx={{ color: '#0A2540', mb: 1 }}>Estructura Académica</Typography>
                <Typography variant="body2" color="text.secondary">Importa la lista de categorías y códigos de los cursos alojados en Moodle.</Typography>
              </Box>
              <Button 
                variant="outlined" 
                color="secondary" 
                startIcon={<PlayArrowIcon />} 
                fullWidth
                onClick={() => triggerSync('courses')}
                disabled={!!activeLogId || connectionStatus === 'disconnected'}
                sx={{ borderRadius: 30, py: 1 }}
              >
                Cargar Cursos
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined" sx={{ height: '100%', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)' } }}>
            <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
              <Box mb={3.5}>
                <Typography variant="subtitle1" fontWeight={750} sx={{ color: '#0A2540', mb: 1 }}>Matrícula & Avatares</Typography>
                <Typography variant="body2" color="text.secondary">Sincroniza alumnos y descarga sus fotos reales en la base local.</Typography>
              </Box>
              <Button 
                variant="outlined" 
                color="secondary" 
                startIcon={<PlayArrowIcon />} 
                fullWidth
                onClick={() => triggerSync('enrollments')}
                disabled={!!activeLogId || connectionStatus === 'disconnected'}
                sx={{ borderRadius: 30, py: 1 }}
              >
                Cargar Alumnos
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined" sx={{ height: '100%', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)' } }}>
            <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
              <Box mb={3.5}>
                <Typography variant="subtitle1" fontWeight={750} sx={{ color: '#0A2540', mb: 1 }}>Boletín de Notas</Typography>
                <Typography variant="body2" color="text.secondary">Extrae notas de tareas, exámenes e ítems evaluados con su feedback.</Typography>
              </Box>
              <Button 
                variant="outlined" 
                color="secondary" 
                startIcon={<PlayArrowIcon />} 
                fullWidth
                onClick={triggerSyncAll}
                disabled={!!activeLogId || connectionStatus === 'disconnected'}
                sx={{ borderRadius: 30, py: 1 }}
              >
                Cargar Notas
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card 
            variant="outlined" 
            sx={{ 
              height: '100%', 
              background: 'linear-gradient(135deg, #7C4DFF 0%, #0070E0 100%)',
              color: '#FFFFFF',
              borderColor: 'transparent',
              boxShadow: '0 4px 16px rgba(124, 77, 255, 0.15)',
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(124, 77, 255, 0.25)'
              }
            }}
          >
            <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
              <Box mb={3.5}>
                <Typography variant="subtitle1" fontWeight={750} color="#FFFFFF" mb={1}>Respaldo Secuencial</Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>Dispara una tarea maestra que respalda todos los módulos simultáneamente.</Typography>
              </Box>
              <Button 
                variant="contained" 
                sx={{ 
                  borderRadius: 30,
                  py: 1.1,
                  backgroundColor: '#FFFFFF', 
                  color: '#7C4DFF',
                  fontWeight: 700,
                  '&:hover': {
                    backgroundColor: '#F8FAFC',
                  }
                }}
                startIcon={<PlayArrowIcon />} 
                fullWidth
                onClick={triggerSyncAll}
                disabled={!!activeLogId || connectionStatus === 'disconnected'}
              >
                Ejecutar Respaldo
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Historic logs */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
        <Typography variant="h6" fontWeight={800} sx={{ color: '#0A2540' }}>
          Historial de Ejecuciones y Auditoría
        </Typography>
        <Box display="flex" gap={1.5}>
          <Button
            variant="outlined"
            onClick={handleExportExcel}
            startIcon={<FileDownloadIcon />}
            sx={{ 
              borderColor: '#00B074', 
              color: '#00B074', 
              borderRadius: 30,
              '&:hover': {
                borderColor: '#008C5C',
                backgroundColor: 'rgba(0, 176, 116, 0.05)'
              }
            }}
          >
            Exportar Logs
          </Button>
          <IconButton onClick={() => fetchLogs(false)} disabled={loadingLogs} sx={{ border: '1px solid #E2E8F0', bgcolor: 'background.paper' }}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {loadingLogs ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress color="secondary" />
        </Box>
      ) : logs.length === 0 ? (
        <Card variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No se registran sincronizaciones aún
          </Typography>
        </Card>
      ) : isMobile ? (
        // Mobile-native responsive list cards
        <Box display="flex" flexDirection="column" gap={2}>
          {logs.map((log) => (
            <Card key={log.id} variant="outlined" sx={{ borderRadius: 3, p: 2, position: 'relative' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Typography variant="subtitle2" fontWeight={750} color="primary.dark">
                  #{log.id} - {getSyncTypeLabel(log.sync_type)}
                </Typography>
                {getStatusChip(log.status)}
              </Box>
              
              <Grid container spacing={1.5} mb={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" display="block">Procesados</Typography>
                  <Typography variant="body2" fontWeight={700}>{log.records_processed}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" display="block">Nuevos / Act</Typography>
                  <Typography variant="body2" fontWeight={700} color="#00B074">
                    {log.records_created} / {log.records_updated}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 1.5 }} />

              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  {new Date(log.started_at).toLocaleDateString()} • {formatDuration(log.started_at, log.completed_at)}
                </Typography>
                {log.status === 'error' && (
                  <Button size="small" color="error" onClick={() => openErrorModal(log)} startIcon={<InfoIcon fontSize="small" />}>
                    Ver Error
                  </Button>
                )}
              </Box>
            </Card>
          ))}
        </Box>
      ) : (
        // Desktop table view
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 4, overflow: 'hidden' }}>
          <Table>
            <TableHead sx={{ backgroundColor: 'rgba(241, 245, 249, 0.6)' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Tipo de Tarea</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Procesados</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Nuevos / Act</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Fecha Inicio</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Duración</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Detalles</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell>#{log.id}</TableCell>
                  <TableCell><Typography fontWeight={700} variant="body2" color="#0A2540">{getSyncTypeLabel(log.sync_type)}</Typography></TableCell>
                  <TableCell>{getStatusChip(log.status)}</TableCell>
                  <TableCell>{log.records_processed}</TableCell>
                  <TableCell>
                    <Box component="span" sx={{ color: '#00B074', fontWeight: 700 }}>{log.records_created}</Box>
                    {' / '}
                    <Box component="span" sx={{ color: 'secondary.main', fontWeight: 700 }}>{log.records_updated}</Box>
                  </TableCell>
                  <TableCell>{new Date(log.started_at).toLocaleString()}</TableCell>
                  <TableCell>{formatDuration(log.started_at, log.completed_at)}</TableCell>
                  <TableCell align="center">
                    {log.status === 'error' ? (
                      <IconButton size="small" color="error" onClick={() => openErrorModal(log)}>
                        <InfoIcon />
                      </IconButton>
                    ) : (
                      <IconButton size="small" sx={{ color: '#00B074' }}>
                        <CheckCircleIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Error Details Dialog */}
      <Dialog 
        open={errorModal.open} 
        onClose={() => setErrorModal(prev => ({ ...prev, open: false }))}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: 'error.main', pb: 1 }}>
          {errorModal.title}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" component="pre" sx={{ 
            fontFamily: 'monospace', 
            backgroundColor: '#FFF5F5', 
            p: 3, 
            borderRadius: 3, 
            whiteSpace: 'pre-wrap',
            color: '#8C1D10',
            border: '1px solid #FFD8D8',
            fontSize: '0.85rem'
          }}>
            {errorModal.content}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setErrorModal(prev => ({ ...prev, open: false }))} variant="contained" color="primary" sx={{ borderRadius: 30, px: 3 }}>
            Entendido
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={toast.open} 
        autoHideDuration={5000} 
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={toast.severity} variant="filled" sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SyncManagerPage;
